'use client';

/**
 * ClientOnlyPostHog - loads PostHog after the page has mounted.
 *
 * Why client-only: `posthog-js` reads `location.protocol` / `location.hash` at
 * module top level. Imported from app/layout.tsx (a Server Component) it was
 * pulled into the SSR bundle and threw `ReferenceError: location is not
 * defined` on every server render (the May 2026 Amplify Lambda cascades), so
 * the import happens in an effect.
 *
 * Why beside the page and not around it (2026-10-16): this used to render
 * `children` and then, once posthog-js loaded, `<Provider>{children}</Provider>`.
 * Changing the element above the whole site made React unmount and remount
 * every page a moment after it appeared: input typed in the first seconds was
 * lost and every effect ran twice. Nothing in the app reads PostHog's React
 * context (events go through posthog.capture), so the tracker renders on its
 * own and the page is never wrapped.
 */

import { Suspense, useEffect, useState, type ComponentType } from 'react';

export function ClientOnlyPostHog() {
  const [Tracker, setTracker] = useState<ComponentType | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      import('./PostHogProvider')
        .then((mod) => {
          if (!cancelled) setTracker(() => mod.PostHogTracker as ComponentType);
        })
        .catch((err) => {
          // Analytics is non-critical; the page carries on without it.
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('[ClientOnlyPostHog] PostHog failed to load:', err);
          }
        });
    };
    // posthog-js is about 200 KB of script: fetch and run it once the browser is
    // idle (or after 4 s), not while the page is still hydrating.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(load, { timeout: 4000 });
    else timer = setTimeout(load, 2500);
    return () => {
      cancelled = true;
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!Tracker) return null;
  // The page-view tracker reads search params, so it has its own boundary.
  return (
    <Suspense fallback={null}>
      <Tracker />
    </Suspense>
  );
}
